// ═══════════════════════════════════════════════════════════════
// FEATURE FLAGS — PublicProposalV2
// ═══════════════════════════════════════════════════════════════
// Grundregel: KEIN Code wird gelöscht. Ausgeblendete Kapitel
// werden ausschließlich über diese Flags aus dem Rendering
// genommen. Jedes Kapitel muss durch Umlegen eines einzigen
// Flags wieder vollständig erscheinen.
// ═══════════════════════════════════════════════════════════════

export const FLAGS = {
  // Kapitel: ausgeblendet, NICHT gelöscht
  showFirstImpression:       true,  // Wiederhergestellt als DEMONSTRATIONS-BEISPIEL für das Minos-Meeting (Alila-Referenzvideo mit Badges + Voiceover). Sobald ein Minos-Video vorliegt, wird nur die Video-URL im Proposal-Datensatz getauscht.
  showStrategicMoment:       false, // "Those who move early define the space"
  showAuthoredDepth:         false, // "This is where visibility becomes authored depth"
  showAwardChapter:          false, // "Never bought. Only earned." + Soulful Places Award
  showGlobalStoryActivation: false, // Globus, zeigt ASSETS 0
  showWhyItPaysOff:          false, // enthält hartkodierte 18.000 EUR
  showStorySystemChapter:    false, // "A place where stories become systems"

  // Einzelelemente
  showPrestigeTier:          false, // ersetzt durch CURATED
  showLiveCountdown:          false, // -> statisches Datum
  showDiscoveryVisit:         false,
  show14DayMissionPricing:    false,
  storyHostingAsSeparateFee:  false,

  // Sanitize Alila references — hide all hardcoded Alila-branded static media (globe video, podcast cover image) so no foreign hotel appears in the MINOS proposal. Grundregel: nichts löschen, nur ausblenden.
  sanitizeAlilaReferences:    true,

  // Storytelling-Deep-Dive-Kapitel: als Erklärkapitel sichtbar (OHNE Preise). Preise stehen ausschließlich im Produktionsmenü unter Pricing.
  showPodcastChapter:         true,  // Podcast Storytelling — Erklärung + Audio-Beispiele
  showAudiovisualChapter:     true,  // Audiovisual Story Episodes — Episoden-Karten-Grid
  showExperienceVisit:        true,  // "Not sure yet?" — Experience Visit chapter (right after Story Episodes)
  showHostedStayChapter:      false, // Editorial Documentary Stay (weiterhin nur im Produktionsmenü)
  showOnsiteChapter:          false, // Soulful On-Site Production (weiterhin nur im Produktionsmenü)
  showFounderVideoButton:     false, // "A Word from Our Founder" CTA — hidden by request (Aug 17), code preserved
  // Preise in Erklärkapiteln unterdrücken — Prices only inside Pricing chapter
  hidePricesInStoryChapters:  true,

  // Founding Advantage: nur diese drei Karten rendern
  foundingCards: ["24m_lock", "regional_exclusivity", "founding_rank"],
};

// ─── Presentation Mode ─────────────────────────────────────────
// URL-Parameter ?present=1 aktiviert den Präsentations-Modus.
// Dieser blendet zusätzlich die Kapitel 2 (Market Reality),
// 3 (Discovered by soulful travelers) und 7 (Build Your Story
// System + Produktionsmenü) aus. Sichtbar bleiben Kapitel:
// 1 (Hero), 4 (Plattform), 5 (First Impression), 6 (7 Stages),
// 8 (Pricing), 9 (ROI), 10 (Founding + CTA).
// ────────────────────────────────────────────────────────────────
export const PRESENTATION_MODE_CHAPTERS = [1, 4, 5, 6, 8, 9, 10];

export const isPresentationMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get("present") === "1";
  } catch { return false; }
};

// ─── Pricing (Source of Truth) ─────────────────────────────────
// Diese Konstanten sind die einzige Preis-Quelle. EN und DE
// müssen exakt identische Zahlen ausgeben. Sprach-Texte nur
// für Labels.
// ────────────────────────────────────────────────────────────────
export const TIERS = [
  { id: "curated",   label: "CURATED",   price: null, notForSale: true,
    badge: "BY INVITATION", subline: "Our investment in selected places.",
    cta: { label: "NOT FOR SALE", disabled: true } },
  { id: "signature", label: "SIGNATURE", price: 36000, currency: "EUR", period: "year", unit: "per property" },
  { id: "iconic",    label: "ICONIC",    price: 60000, currency: "EUR", period: "year", unit: "per property" },
];

// Founding Offer — Rechnung geht auf (36k + 36k = 72k → 60k). Produktionen (Originals) werden IMMER separat gebucht.
export const FOUNDING_OFFER = {
  label: "FOUNDING PARTNERSHIP 2026",
  lineItems: [
    { label: "Minos Palace — Hotels",            price: 36000 },
    { label: "Nao Longevity Hub — Longevity Hotels", price: 36000 },
  ],
  standaloneTotal: 72000,
  price: 60000,
  includes: [
    "Dual placement — Hotels + Longevity Hotels",
    "Travel advisor distribution & preferred integration",
    "12 months hosting, editorial and updates",
    "24-month fee protection",
  ],
};

// Production menu — reine Preisliste. Neue Leiter: 4.5k → 6.5k → 12k (Founding 9.5k). Zusatzsprache 2.5k.
export const PRODUCTION = [
  { id: "voiceover",   label: "Voiceover your finished film", price: 4500,  unit: "per episode · your film + our narration/sound/master" },
  { id: "hybrid_story",label: "Story Episode (Hybrid)",       price: 6500,  unit: "per episode · your material · story build, edit, color, music, narration" },
  { id: "original",    label: "Soulful Places Original (On-Site)", price: 12000, foundingPrice: 9500, unit: "per episode · we travel, we film, full production · hosted stay required" },
  { id: "language",    label: "Additional language",          price: 2500,  unit: "per episode · translation, new voice, new mix" },
  { id: "podcast",     label: "Podcast episode",              price: 6000,  unit: "per episode · first language included" },
  { id: "buyout",      label: "Buyout / usage rights",        custom: true, label2: "on request" },
];

// ─── Static Validity Date ──────────────────────────────────────
export const VALID_UNTIL = "31 August 2026";

// ─── Prestige → Curated backward-compat mapping ────────────────
export const mapLegacyTier = (tid) => (tid === "prestige" ? "curated" : tid);
